import { canUseDOM, isValidValue } from './helper';
import { FEATURE_FLAGS } from '@marriott/mi-feature-flag-utils';

export enum DATALAYER_PAGE_EVENT {
  EARNING_PREFERENCE_SUCCESS = 'earning-preference-success',
  CONVERT_POINTS_SUCCESS = 'convert-points-success',
  CONVERT_POINTS_FAILURE = 'convert-points-failure',
  LOGIN_SUCCESS = 'login-success',
  LOGIN_FAILURE = 'login-failure',
  PROMOTION = 'promotion',
  QRV_FAILURE = 'qrv-failure',
  PHONE_NUMBER_ERROR = 'phone-number-error',
  VERIFICATION_CODE_ERROR = 'VERIFICATION_CODE_ERROR',
  PHONE_ALREADY_ASSOCIATED_ERROR = 'PHONE_ALREADY_ASSOCIATED_ERROR',
}

let ACCOUNT_DATALAYER_COUNTER: number = 0;
const updateDataLayerCounter = () => {
  ACCOUNT_DATALAYER_COUNTER = ACCOUNT_DATALAYER_COUNTER + 1;
};

export const updateDataLayerProperties = (
  dataLayerProperties: { [key: string]: unknown } | undefined,
  pageEvent: string,
  datalayerAction?: DataLayerActionType
) => {
  if (canUseDOM) {
    if (datalayerAction) {
      datalayerAction?.update({
        event: datalayerAction.eventName,
        ...dataLayerProperties,
      });
    } else {
      const dataLayer = window.dataLayer ?? {};
      if (dataLayerProperties) {
        Object.keys(dataLayerProperties).forEach(key => {
          dataLayer[key] = dataLayerProperties[key];
        });
      }
    }
    // update counter before updating hash
    updateDataLayerCounter();
    // Update the window.location.hash with the ACCOUNT_DATALAYER_COUNTER
    window.history.replaceState({}, '', `#page_name=${pageEvent}-${ACCOUNT_DATALAYER_COUNTER}`);
  }
};

/**
 * Extracts the request ID from a string
 * If format is "path~X~requestId", returns the portion after "~X~"
 * Otherwise returns the original value
 * @param requestId - The raw request ID string
 */
export const extractRequestId = (requestId: string | undefined): string => {
  if (!requestId) return '';

  const delimiter = '~X~';
  if (requestId.includes(delimiter)) {
    return requestId.split(delimiter).pop() ?? '';
  }

  return requestId;
};

/**
 * Formats a date/timestamp to the format: YYYYMMDDHHmmssEDT (e.g., 20260120032105EDT)
 * @param timestamp - ISO string or Date object from server
 * @param timezone - Optional timezone (default: 'America/New_York' for EDT/EST)
 * @returns Formatted timestamp string with EDT
 */
export const formatServerTimestamp = (timestamp: string | Date | undefined, timezone = 'America/New_York'): string => {
  if (!timestamp) return '';

  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  if (isNaN(date.getTime())) return '';

  // Format date parts in the specified timezone
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  const parts = dateFormatter.formatToParts(date);
  const getPart = (type: string) => parts.find(p => p.type === type)?.value || '';

  const datePart = `${getPart('year')}${getPart('month')}${getPart('day')}`;
  const timePart = `${getPart('hour')}${getPart('minute')}${getPart('second')}`;

  return `${datePart}${timePart}EDT`;
};

/**
 * Builds enrollment event details string dynamically
 * @param segments - Array of string values to join with pipe separator
 * @returns Formatted string with segments joined by pipes
 */
export const buildEnrollmentEventDetails = (segments: (string | undefined)[]): string => {
  return segments.map(segment => segment ?? '').join('|');
};

export const updateDataLayerAttributes = (
  isDtt?: boolean,
  featureFlags?: Record<string, unknown>,
  requestId?: string,
  serverTimestamp?: string,
  dataLayer?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: Array<Record<string, any>>;
    mvpOffersData: string;
  },
  experienceEnrollmentVariationArg?: string
) => {
  /**
   * update data layer env platform for dtt and enrollment event details
   *
   */
  const ENV_PLATFORM = 'env_platform';
  const ENROLLMENT_EVENT_DETAILS = 'enrollmentEventDetails';
  const ENROLLMENT_ATTEMPT = 'EnrollmentAttempt';
  // Prefer experienceEnrollmentVariationArg over feature flag when available
  const experienceEnrollmentVariation =
    experienceEnrollmentVariationArg ??
    (featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_ACCOUNT_ENROLLMENT_VARIATIONS] as string | undefined);

  if (dataLayer?.data) {
    const originalData = dataLayer?.data[0];
    if (originalData) {
      // Clone the data object to avoid mutation issues
      const data = { ...originalData };

      // Handle env_platform update for DTT
      if (isDtt) {
        const env_str = data[ENV_PLATFORM];
        data[ENV_PLATFORM] = `${env_str}-dtt`;
      }

      // Handle enrollmentEventDetails update with {experienceEnrollmentVariation}|EnrollmentAttempt||{requestId}#{serverTimestamp}
      if (isValidValue(experienceEnrollmentVariation) && requestId) {
        const extractedRequestId = extractRequestId(requestId);
        const formattedTimestamp = formatServerTimestamp(serverTimestamp);
        const requestIdWithTimestamp = formattedTimestamp
          ? `${extractedRequestId}#${formattedTimestamp}`
          : extractedRequestId;
        // Use page_experience_ prefix when from feature flag, "Page " prefix when from DOM
        const variationSegment = `Pattern ${experienceEnrollmentVariation}`;
        // Build enrollment event details dynamically: [variation, eventType, '', requestIdWithTimestamp]
        data[ENROLLMENT_EVENT_DETAILS] = buildEnrollmentEventDetails([
          variationSegment,
          ENROLLMENT_ATTEMPT,
          '',
          requestIdWithTimestamp,
        ]);
      }

      return { ...dataLayer, data: [data] };
    }
    return dataLayer;
  }
  return dataLayer;
};
